import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import config from '@config'
import styled, { css } from 'styled-components'

interface StyledEntityImageProps {
  hasError: boolean
  size: number
}

interface StyledImageProps {
  hasError: boolean
}

export interface EntityImageProps {
  url: string
  name: string
  size: number
}

const {
  backend: { imageUrl },
} = config

const StyledEntityImage = styled.div<StyledEntityImageProps>`
  ${({
    theme: {
      colors: { iconDefaultDisabled },
    },
    hasError,
    size,
  }) => css`
    ${hasError &&
    css`
      background-color: ${iconDefaultDisabled};
      border-radius: 50%;
    `}
    height: ${size}px;
    width: ${size}px;
    min-width: ${size}px;
  `}
`

const StyledImage = styled.img<StyledImageProps>`
  ${({ hasError }) =>
    hasError &&
    css`
      display: none;
    `};
  height: 100%;
  width: 100%;
  object-fit: contain;
`

const EntityImage: FunctionComponent<EntityImageProps> = ({
  url,
  name,
  size,
}) => {
  const [error, setError] = useState<boolean>(false)
  const imageRef = useRef<HTMLImageElement>(null)

  const onError = useCallback(() => {
    setError(true)
  }, [])

  useEffect(() => {
    const currentImage = imageRef.current
    currentImage?.addEventListener('error', onError)
    return () => {
      currentImage?.removeEventListener('error', onError)
    }
  }, [onError])

  const imageSource = `${imageUrl}${url}?ratio=free&imwidth=${size}`

  return (
    <StyledEntityImage hasError={error} size={size}>
      {url && (
        <StyledImage
          ref={imageRef}
          hasError={error}
          srcSet={`${imageSource}&imdensity=1 1x, ${imageSource}&imdensity=2 2x, ${imageSource}&imdensity=3 3x`}
          alt={name}
          loading="lazy"
          title={name}
        />
      )}
    </StyledEntityImage>
  )
}

export default EntityImage
